import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Volume2, MousePointerClick, Palette, Music, Layers, LayoutTemplate, Type, TextCursorInput, ArrowRightLeft, Video, Package, Lock, Folder, Unlock, BoxIcon, GraduationCap } from 'lucide-react';
const folders = [{
  id: 1,
  title: 'Efectos de Sonido',
  icon: Volume2,
  desc: 'Cientos de efectos de sonido profesionales: whooshes, impacts, risers, transiciones y más.'
}, {
  id: 2,
  title: 'Flechas Animadas',
  icon: MousePointerClick,
  desc: 'Flechas y punteros animados para destacar elementos en tus videos.'
}, {
  id: 3,
  title: 'LUTS',
  icon: Palette,
  desc: 'Corrección de color cinematográfica con LUTs profesionales para cualquier software.'
}, {
  id: 4,
  title: 'Música',
  icon: Music,
  desc: 'Tracks libres de copyright para tus proyectos: beats, lo-fi, épica, corporativa y más.'
}, {
  id: 5,
  title: 'Overlays',
  icon: Layers,
  desc: 'Overlays de luz, bokeh, film grain, lens flares y efectos atmosféricos.'
}, {
  id: 6,
  title: 'Plantillas Gráficas',
  icon: LayoutTemplate,
  desc: 'Templates editables para thumbnails, banners, stories y más.'
}, {
  id: 7,
  title: 'Texturas',
  icon: BoxIcon,
  desc: 'Texturas de alta resolución: grunge, papel, metal, madera y más.'
}, {
  id: 8,
  title: 'Tipografías Elegantes',
  icon: Type,
  desc: 'Fuentes premium seleccionadas para títulos impactantes y textos elegantes.'
}, {
  id: 9,
  title: 'Títulos y Textos',
  icon: TextCursorInput,
  desc: 'Animaciones de texto y títulos listos para usar en tus ediciones.'
}, {
  id: 10,
  title: 'Transiciones',
  icon: ArrowRightLeft,
  desc: 'Transiciones cinematográficas: glitch, zoom, swipe, spin y muchas más.'
}, {
  id: 11,
  title: 'Videos de Stock',
  icon: Video,
  desc: 'Clips de video de stock en alta resolución para complementar tus proyectos.'
}, {
  id: 12,
  title: 'Paso a Paso: Editar desde 0',
  icon: GraduationCap,
  desc: 'Guía completa paso a paso para aprender a editar desde cero: importar, cortar, agregar efectos, exportar y más.'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function FeaturesGrid() {
  const [revealed, setRevealed] = useState<number[]>([]);
  const handleReveal = (id: number) => {
    if (!revealed.includes(id)) {
      setRevealed(prev => [...prev, id]);
    }
  };
  const progressPercentage = revealed.length / folders.length * 100;
  return <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto" data-id="element-43">
      {/* Header Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} className="flex flex-col items-center mb-16 text-center" data-id="element-44">
        <div className="inline-flex items-center justify-center p-4 bg-primary/10 rounded-full mb-6 glow-border" data-id="element-45">
          <Package className="w-10 h-10 text-primary" data-id="element-46" />
        </div>
        <h2 className="text-2xl md:text-4xl lg:text-5xl font-black uppercase tracking-widest mb-8" data-id="element-47">
          DESCUBRÍ QUÉ HAY <span className="text-primary" data-id="element-48">DENTRO DEL PACK</span>
        </h2>

        {/* Gamification Progress Bar */}
        <div className="flex flex-col items-center w-full max-w-md gap-3" data-id="element-49">
          <div className="flex justify-between w-full text-sm font-bold" data-id="element-50">
            <span className="text-textSecondary" data-id="element-51">
              Progreso de descubrimiento
            </span>
            <span className="text-primary" data-id="element-52">
              {revealed.length} / {folders.length} carpetas
            </span>
          </div>
          <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden border border-white/10" data-id="element-53">
            <motion.div className="h-full bg-primary shadow-[0_0_10px_rgba(59,130,246,0.8)]" initial={{
            width: 0
          }} animate={{
            width: `${progressPercentage}%`
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-54" />
          </div>
          {revealed.length === folders.length && <motion.span initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-xs font-black text-green-400 uppercase tracking-widest mt-2" data-id="element-55">
              ¡Has descubierto todo el contenido!
            </motion.span>}
        </div>
      </motion.div>

      {/* Grid Section */}
      <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-50px'
    }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-56">
        {folders.map(folder => {
        const isRevealed = revealed.includes(folder.id);
        const Icon = folder.icon;
        return <motion.div key={folder.id} variants={itemVariants} className="relative w-full h-56 md:h-64 cursor-pointer group" style={{
          perspective: 1000
        }} onClick={() => handleReveal(folder.id)} data-id="element-57">
              <motion.div className="w-full h-full relative" style={{
            transformStyle: 'preserve-3d'
          }} animate={{
            rotateY: isRevealed ? 180 : 0
          }} transition={{
            duration: 0.6,
            type: 'spring',
            stiffness: 260,
            damping: 20
          }} data-id="element-58">
                {/* FRONT FACE (Locked) */}
                <div className={`absolute inset-0 bg-surface border rounded-2xl flex flex-col items-center justify-center p-4 transition-colors duration-300 ${isRevealed ? 'border-white/10' : 'border-white/10 hover:border-primary/50 hover:bg-white/5'}`} style={{
              backfaceVisibility: 'hidden'
            }} data-id="element-59">
                  <div className="absolute top-4 right-4" data-id="element-60">
                    <Lock className="w-4 h-4 text-textSecondary/40" data-id="element-61" />
                  </div>
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300" data-id="element-62">
                    <Folder className="w-8 h-8 text-textSecondary group-hover:text-primary transition-colors" data-id="element-63" />
                  </div>
                  <h3 className="text-sm md:text-base font-bold text-center text-textSecondary group-hover:text-white transition-colors" data-id="element-64">
                    {folder.title}
                  </h3>
                  <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-primary mt-4 opacity-0 group-hover:opacity-100 transition-opacity animate-pulse" data-id="element-65">
                    Click para abrir
                  </span>
                </div>

                {/* BACK FACE (Revealed) */}
                <div className="absolute inset-0 bg-surface border border-primary glow-border rounded-2xl flex flex-col items-center justify-center p-4 md:p-6 text-center" style={{
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)'
            }} data-id="element-66">
                  <div className="absolute top-4 right-4" data-id="element-67">
                    <Unlock className="w-4 h-4 text-primary/60" data-id="element-68" />
                  </div>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3" data-id="element-69">
                    <Icon className="w-6 h-6 text-primary" data-id="element-70" />
                  </div>
                  <h3 className="text-sm font-black uppercase tracking-widest mb-2 text-white" data-id="element-71">
                    {folder.title}
                  </h3>
                  <p className="text-xs md:text-sm text-textSecondary font-medium leading-relaxed" data-id="element-72">
                    {folder.desc}
                  </p>
                </div>
              </motion.div>
            </motion.div>;
      })}
      </motion.div>
    </section>;
}